import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCertificate,
  FaCheck,
  FaLock,
  FaChevronRight,
  FaDownload,
} from "react-icons/fa6";
import { toast } from "react-toastify";
import Header from "../components/Header";
import api from "../utils/axiosConfig";
import { downloadBlobFile } from "../utils/downloadBlob";
import {
  CourseDetailView,
  CourseLessonView,
  CourseMaterialView,
} from "../utils/entities/course";
import { resolveResumeLesson, isLessonDone, isLessonExamPending } from "../utils/courseNavigation";
import {
  CoursePageShell,
  CourseBreadcrumb,
  CourseLoading,
  CertificateCodeChip,
  MaterialTypeIcon,
  formatFileSize,
  CourseCelebrateBurst,
} from "../components/courses/CourseUi";

type MaterialWithLesson = CourseMaterialView & {
  lessonId: number;
  lessonTitle: string;
  fileSizeBytes?: number | null;
};

function lessonDisplayTitle(lesson: CourseLessonView, index: number): string {
  const numberLabel = `Aula ${index + 1}`;
  const title = (lesson.title || "").trim();
  if (!title) return numberLabel;
  const lower = title.toLowerCase();
  const numLower = numberLabel.toLowerCase();
  if (lower === numLower || lower.startsWith(`${numLower} `)) {
    return title;
  }
  return `${numberLabel} · ${title}`;
}

const formatLessonDuration = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  if (r === 0) return `${m}min`;
  return `${m}min ${r.toString().padStart(2, "0")}s`;
};

const celebrateKey = (courseId: number) => `course-celebrate-${courseId}`;

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const courseId = Number(id);
  const isValidCourseId = Number.isFinite(courseId) && courseId > 0;
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseDetailView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const [downloadingMaterialId, setDownloadingMaterialId] = useState<
    number | null
  >(null);
  const [celebrate, setCelebrate] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<CourseDetailView>(
        `agent/courses/${courseId}`
      );
      setCourse(data);
      setError(null);
    } catch {
      setError("Não foi possível carregar o curso.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (isValidCourseId) {
      setLoading(true);
      load();
    } else {
      setLoading(false);
      setError("Curso inválido.");
    }
  }, [isValidCourseId, load]);

  useEffect(() => {
    if (!course || course.status !== "completed") return;
    try {
      const key = celebrateKey(courseId);
      if (sessionStorage.getItem(key) === "1") return;
      sessionStorage.setItem(key, "1");
      setCelebrate(true);
      const t = window.setTimeout(() => setCelebrate(false), 1200);
      return () => window.clearTimeout(t);
    } catch {
      // sessionStorage indisponível
    }
  }, [course, courseId]);

  const materials = useMemo((): MaterialWithLesson[] => {
    if (!course) return [];
    const includeAll = course.status === "completed";
    const list: MaterialWithLesson[] = [];
    for (const lesson of course.lessons) {
      if (!includeAll && !lesson.unlocked) continue;
      for (const m of lesson.materials || []) {
        list.push({
          ...m,
          lessonId: lesson.id,
          lessonTitle: lesson.title,
        });
      }
    }
    return list;
  }, [course]);

  const handleDownloadCertificate = useCallback(async () => {
    setDownloadingCert(true);
    try {
      await downloadBlobFile(
        `agent/courses/${courseId}/certificate/download`,
        `certificado-curso-${courseId}.pdf`
      );
    } catch {
      toast.error("Certificado ainda não disponível.");
    } finally {
      setDownloadingCert(false);
    }
  }, [courseId]);

  const handleDownloadMaterial = useCallback(
    async (material: MaterialWithLesson) => {
      setDownloadingMaterialId(material.id);
      try {
        await downloadBlobFile(
          `agent/courses/${courseId}/lessons/${material.lessonId}/materials/${material.id}/download`,
          material.fileName || material.title
        );
      } catch {
        toast.error("Erro ao baixar material.");
      } finally {
        setDownloadingMaterialId(null);
      }
    },
    [courseId]
  );

  const goToResume = () => {
    if (!course) return;
    const resume = resolveResumeLesson(course.lessons);
    if (!resume) return;
    if (isLessonExamPending(resume)) {
      navigate(`/courses/${courseId}/lessons/${resume.id}/exam`);
      return;
    }
    navigate(`/courses/${courseId}/lessons/${resume.id}`);
  };

  const goToReview = () => {
    if (!course || course.lessons.length === 0) return;
    const first = course.lessons.find((l) => l.unlocked) ?? course.lessons[0];
    navigate(`/courses/${courseId}/lessons/${first.id}`);
  };

  if (loading) {
    return (
      <>
        <Header />
        <CourseLoading />
      </>
    );
  }

  if (error || !course) {
    return (
      <>
        <Header />
        <CoursePageShell>
          <CourseBreadcrumb items={[{ label: "Cursos", to: "/courses" }]} />
          <p className="text-red-600">{error ?? "Curso não encontrado."}</p>
        </CoursePageShell>
      </>
    );
  }

  const totalLessons = course.lessons.length;
  const completedLessons = course.lessons.filter((l) => isLessonDone(l)).length;
  const isCompleted = course.status === "completed";
  const hasStarted = isCompleted || completedLessons > 0;
  const showCertificate = isCompleted && course.certificateAvailable;
  const resumeLesson = !isCompleted
    ? resolveResumeLesson(course.lessons)
    : null;
  const progressPct =
    totalLessons > 0
      ? Math.min(100, Math.round((completedLessons / totalLessons) * 100))
      : 0;

  return (
    <>
      <Header />
      <CoursePageShell>
        <div className="max-w-3xl mx-auto w-full">
          <CourseBreadcrumb
            items={[
              { label: "Cursos", to: "/courses" },
              { label: course.title },
            ]}
          />

          <header className="relative mb-6 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <CourseCelebrateBurst active={celebrate} />
            <div className="px-4 py-4 md:px-5">
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                {course.title}
              </h1>
              {course.description && (
                <p className="text-slate-600 text-sm leading-relaxed mt-1">
                  {course.description}
                </p>
              )}
              {totalLessons > 0 && (
                <div className="mt-4">
                  <div
                    className="h-2 w-full rounded-full bg-slate-200 overflow-hidden mb-2"
                    role="progressbar"
                    aria-valuenow={progressPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Progresso do curso"
                  >
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        isCompleted ? "bg-emerald-500" : "bg-preppoBlue"
                      }`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-slate-600 tabular-nums">
                      {completedLessons} de {totalLessons} aulas
                      {isCompleted ? " — 100%" : ""}
                    </p>
                    {!isCompleted && (
                      <p className="text-sm text-slate-500 tabular-nums">
                        {progressPct}%
                      </p>
                    )}
                  </div>
                </div>
              )}

              {(showCertificate ||
                (!isCompleted && totalLessons > 0) ||
                (isCompleted && totalLessons > 0)) && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  {showCertificate ? (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                      <button
                        type="button"
                        onClick={handleDownloadCertificate}
                        disabled={downloadingCert}
                        className="inline-flex items-center justify-center gap-2 self-start bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 text-sm font-semibold shadow-sm transition-all disabled:opacity-60"
                      >
                        <FaCertificate className="text-sm" />
                        {downloadingCert ? "Baixando..." : "Baixar certificado"}
                      </button>
                      {course.certificateVerificationCode && (
                        <CertificateCodeChip
                          code={course.certificateVerificationCode}
                          validateHref={`/certificates/validate/${encodeURIComponent(
                            course.certificateVerificationCode
                          )}`}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2.5">
                      {!isCompleted && totalLessons > 0 ? (
                        <button
                          type="button"
                          onClick={goToResume}
                          className="inline-flex items-center justify-center gap-2 bg-preppoBlue text-white px-4 py-2 rounded-lg hover:bg-preppoBlue/90 text-sm font-semibold shadow-sm transition-all"
                        >
                          {hasStarted ? "Continuar curso" : "Iniciar curso"}
                          <FaChevronRight className="text-xs" />
                        </button>
                      ) : isCompleted && totalLessons > 0 ? (
                        <button
                          type="button"
                          onClick={goToReview}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-800 hover:bg-slate-50 text-sm font-semibold transition-all"
                        >
                          Revisar aulas
                          <FaChevronRight className="text-xs" />
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          </header>

          {totalLessons > 0 && (
            <section className="mb-6">
              <h2 className="text-sm font-bold text-preppoBlue tracking-tight mb-2">
                Aulas do curso
              </h2>
              <ol className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden divide-y divide-slate-100">
                {course.lessons.map((lesson, index) => {
                  const canOpen = lesson.unlocked;
                  const done = isLessonDone(lesson);
                  const examPending = isLessonExamPending(lesson);
                  const examLocked = !!lesson.examLocked && !done;
                  const isResume =
                    !!resumeLesson && resumeLesson.id === lesson.id && !done;
                  const durationSeconds = lesson.video?.durationSeconds ?? 0;
                  return (
                    <li key={lesson.id}>
                      <button
                        type="button"
                        disabled={!canOpen}
                        onClick={() => {
                          if (!canOpen) return;
                          if (examPending) {
                            navigate(
                              `/courses/${courseId}/lessons/${lesson.id}/exam`
                            );
                            return;
                          }
                          navigate(
                            `/courses/${courseId}/lessons/${lesson.id}`
                          );
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-inset focus:ring-preppoBlue/30 ${
                          !canOpen
                            ? "opacity-50 cursor-not-allowed bg-slate-50"
                            : isResume
                            ? "bg-blue-50/60 hover:bg-blue-50"
                            : "hover:bg-slate-50 cursor-pointer"
                        }`}
                      >
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            done
                              ? "bg-emerald-600 text-white"
                              : canOpen
                              ? "bg-preppoBlue/10 text-preppoBlue"
                              : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {done ? (
                            <FaCheck className="text-[10px]" />
                          ) : (
                            index + 1
                          )}
                        </span>
                        <span className="flex-1 min-w-0 font-medium text-slate-900 text-sm tracking-tight truncate">
                          {lessonDisplayTitle(lesson, index)}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          {lesson.hasExam && (
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                examLocked
                                  ? "bg-red-50 text-red-600"
                                  : examPending
                                  ? "bg-amber-50 text-amber-700"
                                  : done
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              {examLocked
                                ? "Prova bloqueada"
                                : examPending
                                ? "Fazer prova"
                                : done
                                ? "Prova ok"
                                : "Com prova"}
                            </span>
                          )}
                          {durationSeconds > 0 && (
                            <span className="text-xs text-slate-500 tabular-nums">
                              {formatLessonDuration(durationSeconds)}
                            </span>
                          )}
                          {!canOpen && (
                            <FaLock className="text-slate-400 text-xs" />
                          )}
                          {canOpen && isResume && !examPending && (
                            <span className="text-xs font-semibold text-preppoBlue">
                              Continuar
                            </span>
                          )}
                          {canOpen && (
                            <FaChevronRight className="text-slate-400 text-sm" />
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {totalLessons === 0 && (
            <p className="text-slate-500 mb-8 text-sm">
              Este curso não possui aulas.
            </p>
          )}

          {materials.length > 0 && (
            <section>
              <h2 className="text-sm font-bold text-preppoBlue tracking-tight mb-2">
                Materiais de apoio
              </h2>
              <ul className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden divide-y divide-slate-100">
                {materials.map((material) => {
                  const sizeLabel = formatFileSize(material.fileSizeBytes);
                  return (
                    <li key={`${material.lessonId}-${material.id}`}>
                      <button
                        type="button"
                        onClick={() => handleDownloadMaterial(material)}
                        disabled={downloadingMaterialId === material.id}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-preppoBlue/30"
                      >
                        <MaterialTypeIcon
                          fileName={material.fileName}
                          mimeType={material.mimeType}
                          size="sm"
                          className="!h-7 !w-7 rounded-full"
                        />
                        <span className="flex-1 min-w-0 font-medium text-slate-900 text-sm tracking-tight truncate">
                          {material.title}
                          {sizeLabel ? (
                            <span className="ml-1.5 font-normal text-slate-400">
                              {sizeLabel}
                            </span>
                          ) : null}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-preppoBlue shrink-0">
                          <FaDownload />
                          {downloadingMaterialId === material.id
                            ? "Baixando..."
                            : "Baixar"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      </CoursePageShell>
    </>
  );
};

export default CourseDetail;
